import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Bell, X, Check, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);

  const unreadCount = notifications.filter(n => !n.read).length;

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/notifications`, { withCredentials: true });
      setNotifications(data);
    } catch {
      // Polling failures are non-critical; don't disrupt UX.
      // Notifications will retry on next 30s interval.
      setNotifications(prev => prev);
    }
  }, []);

  useEffect(() => {
    if (user) fetchNotifications();
    const interval = setInterval(() => { if (user) fetchNotifications(); }, 30000);
    return () => clearInterval(interval);
  }, [user, fetchNotifications]);

  const markRead = async (id) => {
    await axios.put(`${API}/api/notifications/${id}/read`, {}, { withCredentials: true });
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const markAllRead = async () => {
    await axios.put(`${API}/api/notifications/read-all`, {}, { withCredentials: true });
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const handleClick = (notif) => {
    markRead(notif.id);
    if (notif.quote_id) {
      navigate(user?.role === 'admin' ? '/admin' : '/dashboard');
    }
    setOpen(false);
  };

  if (!user) return null;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 border-2 border-slate-900 rounded-lg hover:bg-slate-100 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" strokeWidth={2.5} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#FF5722] text-white text-xs font-bold rounded-full flex items-center justify-center border border-white" data-testid="notification-count">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-12 w-80 sm:w-96 z-50 ss-card max-h-96 overflow-hidden flex flex-col" data-testid="notification-dropdown">
            <div className="p-3 border-b-2 border-slate-200 flex items-center justify-between bg-slate-50">
              <h3 className="font-bold text-sm" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Notifications</h3>
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="text-xs font-bold text-[#FF5722] hover:underline" data-testid="mark-all-read-btn">
                  Mark all read
                </button>
              )}
            </div>
            <div className="overflow-y-auto flex-1">
              {notifications.length === 0 ? (
                <div className="p-6 text-center text-slate-500 text-sm">No notifications yet</div>
              ) : (
                notifications.slice(0, 20).map((notif) => (
                  <div
                    key={notif.id}
                    onClick={() => handleClick(notif)}
                    className={`p-3 border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${!notif.read ? 'bg-[#FDE047]/10' : ''}`}
                    data-testid={`notification-item-${notif.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <MessageSquare className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#FF5722]" strokeWidth={2.5} />
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-xs text-slate-900">{notif.title}</p>
                        <p className="text-xs text-slate-600 truncate">{notif.message}</p>
                        <p className="text-[10px] text-slate-400 mt-1">{new Date(notif.created_at).toLocaleString()}</p>
                      </div>
                      {!notif.read && <span className="w-2 h-2 bg-[#FF5722] rounded-full flex-shrink-0 mt-1" />}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
